import { toast, ToastOptions } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Divider, SelectItem } from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import { DateRangePickerValue } from '@mantine/dates';
import dayjs from 'dayjs';
import Layout from '../components/Layout';
import { Title } from '../components/Title';
import useGetAppointments from '../hooks/useGetAppointments';
import LoadingPage from './LoadingPage';
import { RequireAuth } from '../components/AuthWrapper/RequireAuth';
import setTitle from '../utils/title';
import ViewAppointmentsList from '../components/ViewAppointmentsList';
import { IAppointment } from '../hooks/promises/getAppointments';
import ViewAppointmentsDateSelect from '../components/ViewAppointmentsDateSelect';
import ViewAppointmentsCountSelect from '../components/ViewAppointmentsCountSelect';
import {
  createDefaultRange,
  validateSelectedRange,
} from '../components/ViewAppointmentsList/helpers';

const numOfAppointmentsDisplayedList: SelectItem[] = [
  { value: '20', label: '20 Most Recently Created' },
  { value: '50', label: '50 Most Recently Created' },
  { value: '100', label: '100 Most Recently Created' },
  { value: '200', label: '200 Most Recently Created' },
];

const toastOptions: ToastOptions = { position: 'bottom-right' };

function ViewAppointmentsPageUnsecured() {
  const navigate = useNavigate();

  const defaultDateRange = createDefaultRange();

  const [numOfAppointmentsDisplayed, setNumOfAppointmentsDisplayed] = useState(
    numOfAppointmentsDisplayedList[0].value
  );
  const [dateRange, setDateRange] =
    useState<DateRangePickerValue>(defaultDateRange);

  const { appointments, loading, error, removeAppointment } =
    useGetAppointments(
      numOfAppointmentsDisplayed,
      dayjs(dateRange[0]).format('YYYY-MM-DD'),
      dayjs(dateRange[1]).format('YYYY-MM-DD')
    );
  const [selectedDateRange, setSelectedDateRange] =
    useState<DateRangePickerValue>(dateRange);

  const [appointmentSelectedForDeletion, setAppointmentSelectedForDeletion] =
    useState<IAppointment | null>(null);

  const deleteAppointment = useCallback(() => {
    if (!appointmentSelectedForDeletion) return;
    removeAppointment(appointmentSelectedForDeletion.appointmentId)
      .then(() => {
        toast.success('Succesfully delete appointment', toastOptions);
      })
      .then(() => setTimeout(() => navigate(0), 1000))
      .catch(() => {
        toast.error('Failed to delete appointment', toastOptions);
      });
  }, [appointmentSelectedForDeletion]);

  useEffect(() => {
    if (validateSelectedRange(selectedDateRange, dateRange)) {
      setDateRange(selectedDateRange);
    }
  }, [selectedDateRange]);

  return (
    <Layout className="flex flex-col gap-6">
      <Title text="View Appointments" />
      <div className="grid grid-cols-2 gap-4">
        <ViewAppointmentsDateSelect
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={setSelectedDateRange}
        />
        <ViewAppointmentsCountSelect
          numOfAppointmentsDisplayed={numOfAppointmentsDisplayed}
          numOfAppointmentsDisplayedList={numOfAppointmentsDisplayedList}
          setNumOfAppointmentsDisplayed={setNumOfAppointmentsDisplayed}
        />
      </div>
      <Divider />
      {loading ? (
        <LoadingPage />
      ) : (
        <ViewAppointmentsList
          appointments={appointments}
          appointmentSelectedForDeletion={appointmentSelectedForDeletion}
          deleteAppointment={deleteAppointment}
          setAppointmentSelectedForDeletion={setAppointmentSelectedForDeletion}
          appointmentsError={error}
        />
      )}
    </Layout>
  );
}

function ViewAppointmentsPage() {
  setTitle('View Appointments');
  return (
    <RequireAuth>
      <ViewAppointmentsPageUnsecured />
    </RequireAuth>
  );
}

export default ViewAppointmentsPage;
